import { ref, type Ref, onUnmounted } from 'vue';
import { useQuotaCookie } from './useQuotaCookie';
import Cookies from 'js-cookie';
import axios from 'axios';

export interface QuotaData {
    uuid: string;
    checkoutUrl: string;
    hotelName: string;
    hotelCover: string;
    countryName: string;
    resortName: string;
}

export function useQuota() {
    const abortController = ref<AbortController | null>(null);
    const quotaData: Ref<QuotaData | null> = ref(null);
    const isLoading: Ref<boolean> = ref(false);
    const error: Ref<string | null> = ref(null);

    const quotaCookie = useQuotaCookie();

    const getQuotaCookie = (): QuotaData | null => {
        try {
            const cookieValue = Cookies.get('quotaInfo');
            if (cookieValue) {
                return JSON.parse(cookieValue);
            }
        } catch (e) {
            console.error('Error parsing quotaInfo cookie:', e);
        }
        return null;
    };

    const setQuotaCookie = (data: QuotaData, domain: string) => {
        quotaCookie.setQuotaCookie(data, domain);
        quotaData.value = data;
    };

    const removeQuotaCookie = () => {
        const cookieData = getQuotaCookie();
        const domain = cookieData?.checkoutUrl ? (window as any).appConfig?.cookieDomain : undefined;
        quotaCookie.removeQuotaCookie(domain);
        quotaData.value = null;
    };

    const checkQuota = async (uuid: string): Promise<boolean> => {
        if (abortController.value) {
            abortController.value.abort();
        }

        abortController.value = new AbortController();

        try {
            isLoading.value = true;
            error.value = null;

            const apiGatewayUrl = (window as any).appConfig?.apiGatewayUrl;

            const response = await axios.get(
                `${apiGatewayUrl}/frontend/tour-product/quotas/${encodeURIComponent(uuid)}/validate`,
                {
                    headers: {
                        'Accept': 'application/json',
                    },
                    signal: abortController.value.signal
                }
            );

            return response.data.success === true && response.data.result?.isValidForCheckout === true;
        } catch (e: any) {
            if (axios.isAxiosError && axios.isAxiosError(e)) {
                error.value = e.response?.statusText || e.message;
            } else {
                error.value = e instanceof Error ? e.message : 'Unknown error';
            }
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    onUnmounted(() => {
        if (abortController.value) {
            abortController.value.abort();
            abortController.value = null;
        }
    });

    const validateQuotaFromCookie = async (): Promise<boolean> => {
        const cookieData = getQuotaCookie();

        if (!cookieData || !cookieData.uuid) {
            return false;
        }

        const isValid = await checkQuota(cookieData.uuid);

        if (isValid) {
            quotaData.value = cookieData;
            return true;
        } else {
            quotaData.value = null;
            removeQuotaCookie();
            return false;
        }
    };

    const goToCheckout = () => {
        if (quotaData.value?.checkoutUrl) {
            window.location.href = quotaData.value.checkoutUrl;
        }
    };

    const init = async () => {
        await validateQuotaFromCookie();
    };

    return {
        quotaData,
        isLoading,
        error,
        checkQuota,
        removeQuotaCookie,
        setQuotaCookie,
        validateQuotaFromCookie,
        init,
        goToCheckout
    };
}